import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCourse, useUpdateCourse, useDeleteCourse, useLeaveCourse, useCourseMembers, useGrantEditor, useRevokeEditor, useRemoveMember, useInviteMember } from '../../hooks/useCourses';
import { useVocabulary, useCreateVocabulary, useUpdateVocabulary, useDeleteVocabulary, useBulkCreateVocabulary } from '../../hooks/useVocabulary';
import { useToast } from '../../components/ui/Toast';
import { Button } from '../../components/ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { cn, getPhaseColor } from '../../lib/utils';
import { PhaseDots } from '../../components/brand/PhaseBar';
import { Karti } from '../../components/brand/Karti';
import { KartiCoachBubble, KartiState } from '../../components/brand/KartiCoach';

// Minimum vocabularies required to start a quiz (matches backend QUIZ_CONFIG.MIN_WORDS_REQUIRED)
const MIN_VOCAB_FOR_QUIZ = 15;

type Tab = 'lernen' | 'vokabeln' | 'mitglieder';
const TABS: { id: Tab; label: string }[] = [
  { id: 'lernen', label: 'Lernen' },
  { id: 'vokabeln', label: 'Vokabeln' },
  { id: 'mitglieder', label: 'Mitglieder' },
];

export default function CourseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const courseId = parseInt(id!);

  const { data: course, isLoading: courseLoading } = useCourse(courseId);
  const { data: vocabularies, isLoading: vocabLoading } = useVocabulary(courseId);
  const updateCourseMutation = useUpdateCourse();
  const deleteCourseMutation = useDeleteCourse();
  const leaveCourseMutation = useLeaveCourse();
  const createVocabMutation = useCreateVocabulary();
  const updateVocabMutation = useUpdateVocabulary();
  const deleteVocabMutation = useDeleteVocabulary();
  const bulkCreateMutation = useBulkCreateVocabulary();

  const [showEditCourseDialog, setShowEditCourseDialog] = useState(false);
  const [showAddVocabDialog, setShowAddVocabDialog] = useState(false);
  const [showEditVocabDialog, setShowEditVocabDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [courseName, setCourseName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [editingVocab, setEditingVocab] = useState<any>(null);
  const [tab, setTab] = useState<Tab>('lernen');
  const [showMenu, setShowMenu] = useState(false);

  const canEdit = course && ((course as any).role === 'owner' || (course as any).role === 'editor');
  const isOwner = course && (course as any).role === 'owner';
  const isMember = course && !!(course as any).role;

  const handleEditCourse = () => {
    if (course) {
      setCourseName(course.name);
      setIsPrivate(course.isPrivate);
      setShowEditCourseDialog(true);
    }
  };

  const handleUpdateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateCourseMutation.mutateAsync({
        id: courseId,
        dto: { name: courseName, isPrivate },
      });
      showToast('Kurs aktualisiert', 'success');
      setShowEditCourseDialog(false);
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Aktualisieren', 'error');
    }
  };

  const handleDeleteCourse = async () => {
    try {
      await deleteCourseMutation.mutateAsync(courseId);
      showToast('Kurs gelöscht', 'success');
      navigate('/app/courses');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Löschen', 'error');
    }
  };

  const handleLeaveCourse = async () => {
    try {
      await leaveCourseMutation.mutateAsync(courseId);
      showToast('Kurs verlassen', 'success');
      navigate('/app/courses');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Verlassen', 'error');
    }
  };

  const handleAddVocab = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createVocabMutation.mutateAsync({
        courseId,
        dto: { question, answer },
      });
      showToast('Vokabel hinzugefügt', 'success');
      setShowAddVocabDialog(false);
      setQuestion('');
      setAnswer('');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Hinzufügen', 'error');
    }
  };

  const handleEditVocab = (vocab: any) => {
    setEditingVocab(vocab);
    setQuestion(vocab.question);
    setAnswer(vocab.answer);
    setShowEditVocabDialog(true);
  };

  const handleUpdateVocab = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateVocabMutation.mutateAsync({
        id: editingVocab.id,
        courseId,
        dto: { question, answer },
      });
      showToast('Vokabel aktualisiert', 'success');
      setShowEditVocabDialog(false);
      setQuestion('');
      setAnswer('');
      setEditingVocab(null);
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Aktualisieren', 'error');
    }
  };

  const handleDeleteVocab = async (vocabId: number) => {
    if (!confirm('Vokabel wirklich löschen?')) return;
    try {
      await deleteVocabMutation.mutateAsync({ id: vocabId, courseId });
      showToast('Vokabel gelöscht', 'success');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Löschen', 'error');
    }
  };

  const handleBulkImport = async (e: React.FormEvent) => {
    e.preventDefault();
    const lines = bulkText.trim().split('\n').filter((l) => l.trim());

    // Detect separator: try colon first, then semicolon
    const firstLine = lines[0] || '';
    const separator = firstLine.includes(':') ? ':' : ';';

    const vocabularies = lines.map((line) => {
      const [question, answer] = line.split(separator).map((s) => s.trim());
      return { question, answer };
    });

    if (vocabularies.some((v) => !v.question || !v.answer)) {
      showToast('Format ungültig. Verwenden Sie: Frage:Antwort oder Frage;Antwort', 'error');
      return;
    }

    if (vocabularies.length > 500) {
      showToast('Maximal 500 Vokabeln erlaubt', 'error');
      return;
    }

    try {
      await bulkCreateMutation.mutateAsync({
        courseId,
        dto: { vocabularies },
      });
      showToast(`${vocabularies.length} Vokabeln importiert`, 'success');
      setShowBulkImportDialog(false);
      setBulkText('');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Import', 'error');
    }
  };

  if (courseLoading || vocabLoading) {
    return (
      <div className="space-y-5" aria-busy="true">
        <KartiState screen="loading" size={72} />
        <div className="skeleton h-11 w-28" />
        <div className="flex items-end gap-4">
          <div className="skeleton h-[76px] w-[76px] !rounded-[20px]" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-3 w-24" />
            <div className="skeleton h-8 w-48" />
          </div>
        </div>
        <div className="skeleton h-12 w-full" />
        <div className="skeleton h-44 w-full !rounded-panel" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="ink-card space-y-4 p-6">
        <KartiState screen="notFound" />
        <div className="text-center">
          <p className="mb-3 font-bold">Kurs nicht gefunden</p>
          <Button size="sm" onClick={() => navigate('/app/courses')}>Zu den Kursen</Button>
        </div>
      </div>
    );
  }

  const vocabCount = vocabularies?.length || 0;
  const canStartQuiz = vocabCount >= MIN_VOCAB_FOR_QUIZ;
  const memberCount = (course as any).memberCount || 0;
  const roleLabel = isOwner ? 'OWNER' : canEdit ? 'EDITOR' : isMember ? 'MITGLIED' : 'GAST';
  const histogram = Array.from({ length: 7 }, (_, i) =>
    (vocabularies || []).filter((v) => (v.phase ?? 1) === i + 1).length
  );
  const maxCount = Math.max(1, ...histogram);
  const learned = histogram[5] + histogram[6];
  const inProgress = histogram[2] + histogram[3] + histogram[4];
  const tabIndex = TABS.findIndex((t) => t.id === tab);

  const modes = [
    { n: '01', mode: 'FLASHCARD', name: 'Karteikarten', desc: 'Umdrehen und swipen', accent: true },
    { n: '02', mode: 'MULTIPLE_CHOICE', name: 'Multiple Choice', desc: 'Vier Antworten, eine stimmt', accent: false },
    { n: '03', mode: 'TEST', name: 'Test', desc: 'Antwort selbst tippen', accent: false },
  ];

  return (
    <div className="space-y-[18px]">
      {/* Nav row */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/app/courses')}
          className="press flex h-11 items-center rounded-btn border-2 border-ink bg-cream px-3.5 text-[15px] font-bold shadow-hard-sm"
        >
          ‹ Kurse
        </button>
        {isMember && (
          <button
            onClick={() => setShowMenu((v) => !v)}
            aria-label="Kursaktionen"
            aria-expanded={showMenu}
            className="press flex h-11 w-11 items-center justify-center rounded-btn border-2 border-ink bg-cream text-lg font-bold shadow-hard-sm"
          >
            ···
          </button>
        )}
      </div>

      {showMenu && (
        <div className="ink-card flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap">
          {isOwner && (
            <Button variant="outline" onClick={() => { setShowMenu(false); handleEditCourse(); }}>
              Kurs bearbeiten
            </Button>
          )}
          {isMember && !isOwner && (
            <Button variant="outline" onClick={handleLeaveCourse}>
              Kurs verlassen
            </Button>
          )}
          {isOwner && (
            <Button variant="destructive" onClick={() => { setShowMenu(false); setShowDeleteConfirm(true); }}>
              Kurs löschen
            </Button>
          )}
        </div>
      )}

      {/* Title block */}
      <div className="flex items-end gap-4">
        <div className="flex h-[76px] w-[76px] shrink-0 items-center justify-center rounded-[20px] border-2.5 border-ink bg-marker font-term text-[44px] font-black leading-none shadow-hard-4"
          style={{ transform: 'rotate(-5deg)' }}
        >
          {Array.from(course.name.trim())[0] || 'V'}
        </div>
        <div className="min-w-0">
          <div className="label-mono">{roleLabel} · {course.isPrivate ? 'PRIVAT' : 'ÖFFENTLICH'}</div>
          <h1 className="break-words text-[32px] font-extrabold leading-none tracking-[-0.03em]">{course.name}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {vocabCount} Vokabeln · {memberCount} Mitglieder
          </p>
        </div>
      </div>

      {/* Segmented control */}
      <div role="tablist" className="relative grid h-12 grid-cols-3 rounded-[16px] border-2.5 border-ink bg-cream p-1">
        <div
          aria-hidden
          className="absolute bottom-1 left-1 top-1 rounded-[11px] bg-ink transition-transform duration-[350ms] ease-pill"
          style={{ width: 'calc((100% - 8px) / 3)', transform: `translateX(${tabIndex * 100}%)` }}
        />
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'relative z-10 text-sm font-bold transition-colors duration-[250ms]',
              tab === t.id ? 'text-paper' : 'text-ink'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Lernen */}
      {tab === 'lernen' && (
        <div className="space-y-[18px]">
          <div className="ink-panel space-y-3 px-4 pb-3 pt-4">
            <div className="flex items-baseline justify-between gap-3">
              <span className="label-mono">7 Phasen</span>
              <span className="text-[13px] text-muted-foreground">
                {learned} gelernt · {inProgress} in Arbeit
              </span>
            </div>
            <div className="grid h-[138px] grid-cols-7 items-end gap-[7px] border-b-2 border-ink">
              {histogram.map((c, i) => (
                <div key={i} className="flex h-full flex-col items-center justify-end">
                  <span className="mb-1 font-mono text-[11px] font-bold">{c}</span>
                  <div
                    className="w-full origin-bottom animate-growy rounded-t-[8px] rounded-b-[4px] border-2 border-ink"
                    style={{
                      height: Math.max(4, (c / maxCount) * 104),
                      background: getPhaseColor(i + 1),
                      animationDelay: `${i * 60}ms`,
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-[7px]">
              {histogram.map((_, i) => (
                <span key={i} className="text-center font-mono text-[11px] font-bold text-muted-foreground">
                  P{i + 1}
                </span>
              ))}
            </div>
          </div>

          {isMember && !canStartQuiz && (
            <div className="ink-card flex items-center gap-3 p-4">
              <Karti size={56} mood="sad" />
              <div>
                <div className="font-bold">Noch {MIN_VOCAB_FOR_QUIZ - vocabCount} Vokabeln fehlen</div>
                <div className="text-sm text-muted-foreground">Min. {MIN_VOCAB_FOR_QUIZ} erforderlich</div>
              </div>
            </div>
          )}

          {isMember && <KartiCoachBubble screen="courseDetail" />}

          {isMember && (
            <div className="space-y-3">
              {modes.map((m) => (
                <button
                  key={m.mode}
                  disabled={!canStartQuiz}
                  onClick={() => navigate(`/app/courses/${courseId}/quiz`, { state: { mode: m.mode } })}
                  className={cn(
                    'press flex min-h-[68px] w-full items-center gap-3.5 rounded-list border-2.5 border-ink px-4 text-left shadow-hard disabled:opacity-50',
                    m.accent ? 'bg-marker' : 'bg-cream'
                  )}
                >
                  <span className="font-mono text-xs font-bold">{m.n}</span>
                  <span className="flex-1">
                    <span className="block text-[17px] font-bold">{m.name}</span>
                    <span className="block text-[13px] text-body">{m.desc}</span>
                  </span>
                  <span className="text-xl font-extrabold">→</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Vokabeln */}
      {tab === 'vokabeln' && (
        <div className="space-y-3">
          {canEdit && (
            <div className="flex flex-wrap gap-3">
              <Button variant="accent" size="sm" onClick={() => setShowAddVocabDialog(true)}>
                + Vokabel
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowBulkImportDialog(true)}>
                Bulk-Import
              </Button>
            </div>
          )}
          <div className="ink-panel overflow-hidden">
            {!vocabularies || vocabularies.length === 0 ? (
              <div className="flex flex-col items-center gap-3 p-8 text-center">
                <Karti size={96} mood="sleep" />
                <h3 className="text-xl font-extrabold">Noch keine Vokabeln</h3>
                <p className="text-sm text-body">Füge deine ersten Vokabeln hinzu, um mit dem Lernen zu starten.</p>
                {canEdit && (
                  <Button onClick={() => setShowAddVocabDialog(true)}>Erste Vokabel hinzufügen</Button>
                )}
              </div>
            ) : (
              vocabularies.map((vocab) => (
                <div
                  key={vocab.id}
                  className="flex items-center gap-3 border-b-[1.5px] border-divider px-3.5 py-3 last:border-b-0"
                >
                  <div className="w-14 shrink-0 break-words font-term text-2xl font-black leading-tight">
                    {vocab.question}
                  </div>
                  <div className="min-w-0 flex-1 text-[15px] font-bold">{vocab.answer}</div>
                  {vocab.phase !== undefined && <PhaseDots phase={vocab.phase} size={7} className="hidden xs:flex" />}
                  {canEdit && (
                    <div className="flex shrink-0 gap-1.5">
                      <button
                        onClick={() => handleEditVocab(vocab)}
                        aria-label="Bearbeiten"
                        className="flex h-9 items-center justify-center rounded-tile border-2 border-ink bg-cream px-2.5 text-[13px] font-bold hover:bg-marker"
                      >
                        Ändern
                      </button>
                      <button
                        onClick={() => handleDeleteVocab(vocab.id)}
                        aria-label="Löschen"
                        className="flex h-9 w-9 items-center justify-center rounded-tile border-2 border-ink bg-cream text-sm font-bold hover:bg-wrong"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Tab: Mitglieder */}
      {tab === 'mitglieder' && <MembersTab courseId={courseId} isOwner={!!isOwner} />}

      {/* Edit Course Dialog */}
      <Dialog open={showEditCourseDialog} onOpenChange={setShowEditCourseDialog}>
        <DialogContent>
          <form onSubmit={handleUpdateCourse}>
            <DialogHeader>
              <DialogTitle>Kurs bearbeiten</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="editCourseName" className="label-mono">
                  Kursname
                </label>
                <Input
                  id="editCourseName"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="editVisibility" className="label-mono">
                  Sichtbarkeit
                </label>
                <Select
                  id="editVisibility"
                  value={isPrivate ? 'private' : 'public'}
                  onChange={(e) => setIsPrivate(e.target.value === 'private')}
                >
                  <option value="public">Öffentlich</option>
                  <option value="private">Privat</option>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditCourseDialog(false)}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={updateCourseMutation.isPending}>
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Vocabulary Dialog */}
      <Dialog open={showAddVocabDialog} onOpenChange={setShowAddVocabDialog}>
        <DialogContent>
          <form onSubmit={handleAddVocab}>
            <DialogHeader>
              <DialogTitle>Vokabel hinzufügen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="question" className="label-mono">
                  Frage
                </label>
                <Input
                  id="question"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="z.B. Hello"
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="answer" className="label-mono">
                  Antwort
                </label>
                <Input
                  id="answer"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="z.B. Hallo"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddVocabDialog(false)}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={createVocabMutation.isPending}>
                Hinzufügen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Vocabulary Dialog */}
      <Dialog open={showEditVocabDialog} onOpenChange={setShowEditVocabDialog}>
        <DialogContent>
          <form onSubmit={handleUpdateVocab}>
            <DialogHeader>
              <DialogTitle>Vokabel bearbeiten</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="editQuestion" className="label-mono">
                  Frage
                </label>
                <Input
                  id="editQuestion"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="editAnswer" className="label-mono">
                  Antwort
                </label>
                <Input
                  id="editAnswer"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditVocabDialog(false)}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={updateVocabMutation.isPending}>
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent>
          <form onSubmit={handleBulkImport}>
            <DialogHeader>
              <DialogTitle>Bulk-Import</DialogTitle>
              <DialogDescription>
                Importieren Sie mehrere Vokabeln auf einmal. Format: Eine Vokabel pro Zeile,
                Frage und Antwort getrennt durch Doppelpunkt (:) oder Semikolon (;)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder="meadow:Wiese&#10;reliable:zuverlässig&#10;to accomplish:erreichen&#10;&#10;Oder mit Semikolon:&#10;Hello;Hallo&#10;Good morning;Guten Morgen"
                rows={10}
                required
              />
              <p className="text-sm text-muted-foreground">
                Max. 500 Vokabeln pro Import
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowBulkImportDialog(false)}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={bulkCreateMutation.isPending}>
                Importieren
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kurs löschen?</DialogTitle>
            <DialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Alle Vokabeln und
              Fortschrittsdaten werden gelöscht.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCourse}
              disabled={deleteCourseMutation.isPending}
            >
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MembersTab({ courseId, isOwner }: { courseId: number; isOwner: boolean }) {
  const { showToast } = useToast();
  const { data: members, isLoading, isError, refetch } = useCourseMembers(courseId);
  const grantEditor = useGrantEditor();
  const revokeEditor = useRevokeEditor();
  const removeMember = useRemoveMember();
  const inviteMember = useInviteMember();
  const [showInvite, setShowInvite] = useState(false);
  const [nickname, setNickname] = useState('');
  const [inviteError, setInviteError] = useState('');

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const nick = nickname.trim();
    if (!nick) {
      setInviteError('Bitte gib einen Nickname ein');
      return;
    }
    setInviteError('');
    try {
      const res = await inviteMember.mutateAsync({ courseId, nickname: nick });
      showToast(`${res.displayname || nick} wurde eingeladen`, 'success');
      setShowInvite(false);
      setNickname('');
    } catch (error: any) {
      const status = error.response?.status;
      setInviteError(
        status === 404
          ? 'Nutzer nicht gefunden'
          : status === 400
            ? 'Nutzer ist bereits Mitglied dieses Kurses'
            : error.response?.data?.error || 'Fehler beim Einladen'
      );
    }
  };

  const toggleEditor = async (userId: number, isEditor: boolean) => {
    try {
      if (isEditor) {
        await revokeEditor.mutateAsync({ courseId, userId });
        showToast('Editor-Rechte entzogen', 'success');
      } else {
        await grantEditor.mutateAsync({ courseId, userId });
        showToast('Editor-Rechte erteilt', 'success');
      }
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Ändern der Rechte', 'error');
    }
  };

  const handleRemove = async (userId: number) => {
    if (!confirm('Mitglied wirklich entfernen?')) return;
    try {
      await removeMember.mutateAsync({ courseId, userId });
      showToast('Mitglied entfernt', 'success');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Entfernen', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2.5" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="skeleton h-16 w-full !rounded-[16px]" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="ink-card flex items-center gap-3 p-4">
        <span className="h-3 w-3 shrink-0 rounded-full border-2 border-ink bg-wrong" />
        <p className="flex-1 font-bold">Mitglieder konnten nicht geladen werden</p>
        <Button size="sm" onClick={() => refetch()}>Erneut versuchen</Button>
      </div>
    );
  }

  const sorted = [...(members || [])]
    .filter((m) => !m.removedAt)
    .sort((a, b) => Number(b.isOwner) - Number(a.isOwner) || Number(b.isEditor) - Number(a.isEditor));

  return (
    <div className="space-y-2.5">
      {sorted.map((m) => {
        const name = m.user?.displayname || m.user?.nickname || `Nutzer ${m.userId}`;
        const role = m.isOwner ? 'OWNER' : m.isEditor ? 'EDITOR' : 'MITGLIED';
        return (
          <div
            key={m.userId}
            className="flex flex-wrap items-center gap-3 rounded-[16px] border-2.5 border-ink bg-cream px-3.5 py-3"
          >
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-ink text-base font-extrabold',
                m.isOwner ? 'bg-marker' : 'bg-cream'
              )}
            >
              {Array.from(name)[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1 truncate text-base font-bold">{name}</div>
            <span className="rounded-chip border-[1.5px] border-ink px-[7px] py-1 font-mono text-[10px] font-bold uppercase tracking-[0.06em]">
              {role}
            </span>
            {isOwner && !m.isOwner && (
              <div className="flex w-full gap-2 sm:w-auto">
                <button
                  onClick={() => toggleEditor(m.userId, m.isEditor)}
                  disabled={grantEditor.isPending || revokeEditor.isPending}
                  className="h-9 flex-1 rounded-tile border-2 border-ink bg-cream px-3 text-[13px] font-bold hover:bg-marker disabled:opacity-50 sm:flex-none"
                >
                  {m.isEditor ? 'Editor-Rechte entziehen' : 'Zum Editor machen'}
                </button>
                <button
                  onClick={() => handleRemove(m.userId)}
                  disabled={removeMember.isPending}
                  className="h-9 rounded-tile border-2 border-ink bg-cream px-3 text-[13px] font-bold hover:bg-wrong disabled:opacity-50"
                >
                  Entfernen
                </button>
              </div>
            )}
          </div>
        );
      })}
      {sorted.length === 0 && <p className="text-sm text-muted-foreground">Keine Mitglieder.</p>}
      {isOwner && (
        <button
          onClick={() => {
            setInviteError('');
            setShowInvite(true);
          }}
          className="press h-[52px] w-full rounded-[16px] border-2.5 border-dashed border-ink text-[15px] font-bold"
        >
          + Mitglied einladen
        </button>
      )}

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <form onSubmit={handleInvite}>
            <DialogHeader>
              <DialogTitle>Mitglied einladen</DialogTitle>
              <DialogDescription>Gib den Nickname des Nutzers ein, den du einladen möchtest.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-4">
              <label htmlFor="inviteNickname" className="label-mono">
                Nickname
              </label>
              <Input
                id="inviteNickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="nickname"
                autoComplete="off"
                autoCapitalize="none"
                required
              />
              {inviteError && (
                <p role="alert" className="flex items-center gap-2 text-sm font-bold">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full border-2 border-ink bg-wrong" />
                  {inviteError}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInvite(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={inviteMember.isPending}>
                {inviteMember.isPending ? 'Lädt...' : 'Einladen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
